"use client";

import { SEGURCLASSIC } from "@/lib/brand";

import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { SolicitudPolizaV3 } from "@/types/solicitud";
import { Search, X } from "lucide-react";
import { CarClassicIcon } from "@/components/ui/segurclassic-icons";
import { useRouter } from "next/navigation";
import { useSolicitudes } from "@/hooks/useSolicitudes";
import { fetchAllSolicitudesOnce } from "@/lib/solicitudesSearch";

const RESULT_LIMIT = 8;

function SearchShortcutBadge() {
  const [label, setLabel] = useState("Ctrl+K");
  useEffect(() => {
    setLabel(navigator.platform.toUpperCase().includes("MAC") ? "⌘K" : "Ctrl+K");
  }, []);
  return (
    <kbd className="hidden sm:inline text-[10px] bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 rounded font-mono">
      {label}
    </kbd>
  );
}

function normalizar(v: string): string {
  return v.toLowerCase().replace(/[\s\-.]/g, "");
}

function coincide(s: SolicitudPolizaV3, q: string): boolean {
  const fields = [
    s.vehiculo_matricula,
    s.conductor_numero_documento,
    s.conductor_nombre,
    s.conductor_apellidos,
    s.email,
    s.telefono,
  ];
  return fields.some(f => f && normalizar(f).includes(q));
}

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  // Se guarda junto al término al que corresponde para poder ignorarlo sin
  // necesidad de "resetearlo" síncronamente dentro del efecto de abajo
  // (evita cascading renders — ver regla react-hooks/set-state-in-effect).
  const [ampliados, setAmpliados] = useState<{ q: string; items: SolicitudPolizaV3[] }>({ q: "", items: [] });
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const { solicitudes } = useSolicitudes();

  // Abrir con Ctrl+K / Cmd+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === "Escape") { setOpen(false); setTerm(""); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Búsqueda instantánea sobre las ~300 solicitudes más recientes (sin
  // consulta a Firestore, solo el listener compartido ya en memoria).
  const localResults = useMemo<SolicitudPolizaV3[]>(() => {
    const q = normalizar(term.trim());
    if (q.length < 2) return [];
    return solicitudes.filter(s => coincide(s, q)).slice(0, RESULT_LIMIT);
  }, [term, solicitudes]);

  // Si la búsqueda instantánea no encuentra suficientes resultados, puede
  // ser porque el cliente es antiguo y ya no está entre las ~300 solicitudes
  // más recientes del listener. Se lanza entonces una consulta puntual (sin
  // límite, no realtime) para no dejarlo invisible — ver solicitudesSearch.ts.
  useEffect(() => {
    const q = normalizar(term.trim());
    if (q.length < 2 || localResults.length >= RESULT_LIMIT) return;
    let active = true;
    const timer = setTimeout(() => {
      fetchAllSolicitudesOnce()
        .then(all => { if (active) setAmpliados({ q, items: all.filter(s => coincide(s, q)) }); })
        .catch(() => {});
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [term, localResults.length]);

  const results = useMemo<SolicitudPolizaV3[]>(() => {
    const q = normalizar(term.trim());
    const extra = ampliados.q === q ? ampliados.items : [];
    if (extra.length === 0) return localResults;
    const yaIncluidos = new Set(localResults.map(s => s.id));
    return [...localResults, ...extra.filter(s => !yaIncluidos.has(s.id))].slice(0, RESULT_LIMIT);
  }, [localResults, ampliados, term]);

  const handleSelect = (sol: SolicitudPolizaV3) => {
    setOpen(false);
    setTerm("");
    router.push(`/emision/${sol.id}`);
  };

  if (!open) {
    return (
      <button
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-sm hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors border border-slate-200 dark:border-slate-700"
      >
        <Search className="h-3.5 w-3.5" />
        <span>Buscar...</span>
        <SearchShortcutBadge />
      </button>
    );
  }

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-16 bg-black/60 backdrop-blur-sm" onClick={() => { setOpen(false); setTerm(""); }}>
      <div
        className="w-full max-w-lg mx-4 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-800">
          <Search className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            value={term}
            onChange={e => setTerm(e.target.value)}
            placeholder="Buscar por matrícula, DNI, nombre..."
            className="flex-1 bg-transparent text-sm outline-none text-slate-900 dark:text-slate-100 placeholder-slate-400"
            autoFocus
          />
          {term && (
            <button onClick={() => setTerm("")}>
              <X className="h-4 w-4 text-slate-400 hover:text-slate-600" />
            </button>
          )}
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto">
          {term.length >= 2 && results.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-slate-400">Sin resultados para &quot;{term}&quot;</div>
          )}
          {results.length > 0 && (
            <ul className="divide-y divide-slate-50 dark:divide-slate-800">
              {results.map(sol => (
                <li key={sol.id}>
                  <button
                    onClick={() => handleSelect(sol)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left"
                  >
                    <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                      <CarClassicIcon className="h-4 w-4" primaryColor={SEGURCLASSIC.green} secondaryColor={SEGURCLASSIC.gold} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {sol.vehiculo_matricula || "Sin matrícula"}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {sol.conductor_nombre} {sol.conductor_apellidos} · {sol.conductor_numero_documento}
                      </p>
                    </div>
                    <span className="text-[10px] text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full flex-shrink-0 capitalize">
                      {sol.estadoKanban?.toLowerCase().replace(/_/g, " ")}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {term.length < 2 && (
            <div className="px-4 py-6 text-center text-sm text-slate-400">
              Escribe al menos 2 caracteres para buscar
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2 border-t border-slate-100 dark:border-slate-800 flex items-center gap-2 text-[11px] text-slate-400">
          <kbd className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded font-mono">ESC</kbd> para cerrar
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

